import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChartBar, Megaphone, Wallet, SignOut, Sparkle, UsersThree, List, X, ClockCounterClockwise, Calculator, HandCoins } from "@phosphor-icons/react";
import NotificationBell from "@/components/NotificationBell";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  // Close mobile drawer whenever route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
      isActive
        ? "bg-blue-600 text-white"
        : "text-slate-300 hover:bg-slate-800 hover:text-white"
    }`;

  const Nav = ({ onLinkClick }) => (
    <nav className="px-3 py-4 space-y-1 flex-1" onClick={onLinkClick}>
      {isAdmin && (
        <NavLink to="/" end className={linkClass} data-testid="nav-dashboard">
          <ChartBar size={18} weight="duotone" /> Dashboard
        </NavLink>
      )}
      <NavLink to="/campaigns" className={linkClass} data-testid="nav-campaigns">
        <Megaphone size={18} weight="duotone" /> Campaigns
      </NavLink>
      <NavLink to="/payments" className={linkClass} data-testid="nav-payments">
        <Wallet size={18} weight="duotone" /> Payments
      </NavLink>
      <NavLink to="/approvals" className={linkClass} data-testid="nav-approvals">
        <HandCoins size={18} weight="duotone" /> Approvals
      </NavLink>
      {isAdmin && (
        <NavLink to="/users" className={linkClass} data-testid="nav-users">
          <UsersThree size={18} weight="duotone" /> Team
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/accounting" className={linkClass} data-testid="nav-accounting">
          <Calculator size={18} weight="duotone" /> Accounting
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/audit-log" className={linkClass} data-testid="nav-audit-log">
          <ClockCounterClockwise size={18} weight="duotone" /> Audit log
        </NavLink>
      )}
    </nav>
  );

  const Brand = () => (
    <div className="flex items-center">
      <img
        src="/fanware-logo.webp"
        alt="Fanware Today"
        className="h-8 w-auto max-w-[180px] object-contain"
        data-testid="sidebar-logo"
      />
    </div>
  );

  const Footer = () => (
    <div className="p-4 border-t border-slate-800">
      <div className="text-xs text-slate-400 mb-2">Signed in as</div>
      <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
      <button
        onClick={handleLogout}
        data-testid="logout-btn"
        className="mt-3 w-full flex items-center gap-2 justify-center py-2 text-xs font-medium text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 rounded-md transition-colors"
      >
        <SignOut size={14} /> Log out
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-slate-900 text-white flex-col shrink-0 sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-slate-800 flex items-center justify-between gap-2">
          <Brand />
          <NotificationBell />
        </div>
        <Nav />
        <Footer />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-slate-900 text-white flex items-center justify-between px-4 border-b border-slate-800">
        <Brand />
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
            className="p-2 rounded-md hover:bg-slate-800 text-slate-200"
          >
            <List size={22} />
          </button>
        </div>
      </header>

      {/* Mobile drawer + overlay */}
      {drawerOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/60 animate-in fade-in-0"
            onClick={() => setDrawerOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside
            className="md:hidden fixed left-0 top-0 bottom-0 z-50 w-72 max-w-[85vw] bg-slate-900 text-white flex flex-col animate-in slide-in-from-left duration-200"
            data-testid="mobile-drawer"
          >
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <Brand />
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="mobile-drawer-close"
                aria-label="Close menu"
                className="p-1.5 rounded-md hover:bg-slate-800 text-slate-300"
              >
                <X size={18} />
              </button>
            </div>
            <Nav />
            <Footer />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden pt-14 md:pt-0 pb-16 md:pb-0">
        <div className="px-4 sm:px-6 md:px-8 py-4 md:py-6 max-w-[1600px]">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 h-14 bg-white border-t border-slate-200 flex items-stretch"
        data-testid="mobile-tab-bar"
      >
        {isAdmin && (
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-700"
              }`
            }
            data-testid="tab-dashboard"
          >
            <ChartBar size={20} weight="duotone" />
            Dashboard
          </NavLink>
        )}
        <NavLink
          to="/campaigns"
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
              isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-700"
            }`
          }
          data-testid="tab-campaigns"
        >
          <Megaphone size={20} weight="duotone" />
          Campaigns
        </NavLink>
        <NavLink
          to="/payments"
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
              isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-700"
            }`
          }
          data-testid="tab-payments"
        >
          <Wallet size={20} weight="duotone" />
          Payments
        </NavLink>
        {isAdmin && (
          <NavLink
            to="/users"
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-700"
              }`
            }
            data-testid="tab-users"
          >
            <UsersThree size={20} weight="duotone" />
            Team
          </NavLink>
        )}
      </nav>
    </div>
  );
}
